const NODE_ENV = process.env['NODE_ENV'];

export const isLocalBuild = NODE_ENV === 'development';
export const isDeployedBuild = NODE_ENV === 'production';

// The same bundle is served on every host, so it picks its environment from the
// hostname at runtime. Keep these lists in sync with the CloudFront aliases the
// NH Web bundle is served on (RingNeighborsConsumerSiteServiceCDK). A deployed
// host in none of these lists is resolved by domain suffix in resolveEnvironment
// rather than silently using the dev config (NHVERTICAL-6316).
export const PROD_HOSTNAMES = [
  'neighbors.ring.com',
  'web.publicsafety.ring.com',
  'en-uk.neighbours.ring.com',
  'en-au.neighbours.ring.com',
];
export const QA_HOSTNAMES = [
  'neighbors.qa.ring.com',
  'web.publicsafety.qa.ring.com',
  'en-uk.neighbours.qa.ring.com',
  'en-au.neighbours.qa.ring.com',
];
export const DEV_HOSTNAMES = [
  'neighbors.dev.ring.com',
  'web.publicsafety.dev.ring.com',
  'en-uk.neighbours.dev.ring.com',
  'en-au.neighbours.dev.ring.com',
];

export type Environment = 'prod' | 'qa' | 'dev';

/**
 * Resolve the environment for a hostname. This is the single source of truth:
 * the config (src/config), the isProd/isQA/isDev flags, the Bugsnag release
 * stage and route gating are all derived from it, so they can never disagree.
 *
 * Known hosts match the explicit lists above. A non-deployed build (local dev
 * server or tests) and localhost are always 'dev'. An unrecognised *deployed*
 * host must neither silently use dev for a prod alias nor silently use prod for
 * a non-prod one (NHVERTICAL-6316); it is resolved by domain suffix and logged:
 *   *.qa.ring.com -> qa, *.dev.ring.com -> dev, (*.)ring.com -> prod,
 *   any other host (e.g. a *.cloudfront.net default domain) -> dev.
 */
export const resolveEnvironment = (
  host: string,
  deployedBuild: boolean = isDeployedBuild,
): Environment => {
  if (PROD_HOSTNAMES.includes(host)) {
    return 'prod';
  }
  if (QA_HOSTNAMES.includes(host)) {
    return 'qa';
  }
  if (DEV_HOSTNAMES.includes(host)) {
    return 'dev';
  }

  // Local dev builds, tests, and localhost are never a deployed environment.
  if (!deployedBuild || host === 'localhost' || host === '127.0.0.1') {
    return 'dev';
  }

  console.error(
    `[config] Unrecognised deployed hostname "${host}"; resolving environment by domain suffix. Add it to PROD_HOSTNAMES/QA_HOSTNAMES/DEV_HOSTNAMES in src/utils/constants.`,
  );

  if (host.endsWith('.qa.ring.com')) {
    return 'qa';
  }
  if (host.endsWith('.dev.ring.com')) {
    return 'dev';
  }
  if (host === 'ring.com' || host.endsWith('.ring.com')) {
    return 'prod';
  }
  // A host we do not own (e.g. a raw *.cloudfront.net domain) must never
  // silently talk to prod backends/auth; fall back to dev.
  return 'dev';
};

const { hostname } = window.location;

export const environment = resolveEnvironment(hostname);

export const isProd = environment === 'prod';
export const isQA = environment === 'qa';
export const isDev = environment === 'dev';
export const isLocalNpss = hostname === 'localnpss.dev.ring.com';
export const isLocalNeighbors = hostname === 'localneighbors.dev.ring.com';
